import React, { useState, useCallback, useEffect } from 'react';
import { Button, Snackbar } from '@mui/joy';
import { Plus as Add, CheckCircle, Warning } from '@phosphor-icons/react';
import {
  usePlaidLink,
  type PlaidLinkOnSuccess,
  type PlaidLinkOnExit,
  type PlaidLinkOptions,
} from 'react-plaid-link';
import { apiAccountingClient } from '../../../lib/apiAccounting';

interface PlaidLinkOpenerProps {
  linkToken: string;
  onSuccess: PlaidLinkOnSuccess;
  onExit: PlaidLinkOnExit;
}

/**
 * Inner component that mounts only when linkToken is available.
 * Automatically opens Plaid Link when the hook becomes ready.
 */
const PlaidLinkOpener: React.FC<PlaidLinkOpenerProps> = ({ linkToken, onSuccess, onExit }) => {
  const hasOpened = React.useRef(false);
  const config: PlaidLinkOptions = {
    token: linkToken,
    onSuccess,
    onExit,
  };

  const { open, ready } = usePlaidLink(config);

  useEffect(() => {
    if (ready && !hasOpened.current) {
      hasOpened.current = true;
      open();
    }
  }, [ready, open]);

  return null;
};

interface PlaidLinkButtonProps {
  onSuccess?: () => void;
  size?: 'sm' | 'md' | 'lg';
}

const PlaidLinkButton: React.FC<PlaidLinkButtonProps> = ({
  onSuccess,
  size = 'md'
}) => {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    color: 'success' | 'danger' | 'warning';
  }>({ open: false, message: '', color: 'success' });

  const showSnackbar = (message: string, color: 'success' | 'danger' | 'warning') => {
    setSnackbar({ open: true, message, color });
  };

  const handleClick = async () => {
    try {
      setLoading(true);
      const response = await apiAccountingClient.createLinkToken();
      setLinkToken(response.link_token);
    } catch (error) {
      console.error('Failed to create link token:', error);
      showSnackbar('Failed to initialize bank connection. Please try again.', 'danger');
      setLoading(false);
    }
  };

  const handlePlaidSuccess: PlaidLinkOnSuccess = useCallback(
    async (publicToken, metadata) => {
      try {
        setLoading(true);

        await apiAccountingClient.exchangePublicToken({
          public_token: publicToken,
          institution_id: metadata.institution?.institution_id || '',
          institution_name: metadata.institution?.name || '',
          account_ids: metadata.accounts.map(acc => acc.id),
        });

        setLinkToken(null);
        showSnackbar('Bank account connected successfully!', 'success');

        if (onSuccess) {
          onSuccess();
        }
      } catch (error) {
        console.error('Failed to exchange public token:', error);
        showSnackbar('Failed to connect bank account. Please try again.', 'danger');
      } finally {
        setLoading(false);
      }
    },
    [onSuccess]
  );

  const handlePlaidExit: PlaidLinkOnExit = useCallback((error) => {
    if (error) {
      console.error('Plaid Link error:', error);

      if (error.error_code === 'INVALID_LINK_TOKEN') {
        showSnackbar('Session expired. Please try connecting again.', 'warning');
      } else if (error.error_code === 'USER_SETUP_REQUIRED') {
        showSnackbar('Additional setup required with your bank. Please contact support.', 'warning');
      }
    }

    setLinkToken(null);
    setLoading(false);
  }, []);

  return (
    <>
      <Button
        size={size}
        startDecorator={<Add />}
        onClick={handleClick}
        loading={loading}
        disabled={loading}
      >
        Connect Bank Account
      </Button>

      {linkToken && (
        <PlaidLinkOpener
          linkToken={linkToken}
          onSuccess={handlePlaidSuccess}
          onExit={handlePlaidExit}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        color={snackbar.color}
        variant="soft"
        startDecorator={snackbar.color === 'success' ? <CheckCircle /> : <Warning />}
      >
        {snackbar.message}
      </Snackbar>
    </>
  );
};

export default PlaidLinkButton;
